/**
 * AgendaCalendar Component - Vue semaine/mois avec créneaux récurrents
 * Advanced calendar component for Pro and Multi plans
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  ChevronLeft, ChevronRight, Calendar, Clock, Plus, X, 
  Check, Repeat, CalendarDays, Eye, Settings
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// French day names
const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const DAYS_FULL_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// Generate time slots for a day
const generateTimeSlots = (startHour = 8, endHour = 19, interval = 30) => {
  const slots = [];
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += interval) {
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }
  return slots;
};

const AgendaCalendar = ({ 
  appointments = [], 
  schedule, 
  subscriptionPlan = 'essentiel',
  onSlotClick,
  onAppointmentClick,
  onRecurringSlotSave,
  recurringSlots = []
}) => {
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [newRecurringSlot, setNewRecurringSlot] = useState({
    day_of_week: 1, // Monday
    start_time: '09:00',
    end_time: '10:00',
    slot_type: 'consultation',
    is_recurring: true,
    repeat_weekly: true
  });

  // Check if user has access to advanced features
  const hasAdvancedFeatures = subscriptionPlan === 'pro' || subscriptionPlan === 'multi';

  // Get week dates
  const getWeekDates = (date) => {
    const start = new Date(date);
    const dayOfWeek = start.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Start from Monday
    start.setDate(start.getDate() + diff);
    
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  };

  // Get month dates (including overflow from prev/next months)
  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0
    const dates = [];
    
    // Previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(firstDay);
      d.setDate(d.getDate() - i - 1);
      dates.push({ date: d, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      dates.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month days to complete the grid (6 rows)
    const remaining = 42 - dates.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(lastDay);
      d.setDate(lastDay.getDate() + i);
      dates.push({ date: d, isCurrentMonth: false });
    }
    
    return dates;
  };

  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const monthDates = useMemo(() => getMonthDates(currentDate), [currentDate]);
  const timeSlots = useMemo(() => generateTimeSlots(8, 20, 30), []);

  // Navigation
  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => apt.date?.split('T')[0] === dateStr);
  };

  // Get appointments for a specific time slot
  const getAppointmentsForSlot = (date, time) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => 
      apt.date?.split('T')[0] === dateStr && 
      apt.time === time
    );
  };

  // Check if a slot is blocked (vacation, holiday)
  const isBlockedDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return schedule?.blocked_dates?.includes(dateStr);
  };

  // Check if clinic is open on this day
  const isDayOpen = (date) => {
    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1; // Convert to Mon=0 format
    const daySchedule = schedule?.weekly_schedule?.[dayIndex];
    return daySchedule?.is_open ?? true;
  };

  // Get recurring slots for a day of week
  const getRecurringSlotsForDay = (dayOfWeek) => {
    return recurringSlots.filter(slot => slot.day_of_week === dayOfWeek);
  };

  // Handle slot click
  const handleSlotClick = (date, time) => {
    if (isBlockedDate(date) || !isDayOpen(date)) return;
    
    if (onSlotClick) {
      onSlotClick({ date, time });
    }
    setSelectedSlot({ date, time });
  };

  // Save recurring slot
  const handleSaveRecurringSlot = async () => {
    if (!hasAdvancedFeatures) {
      toast.error('Fonctionnalité disponible avec les forfaits Pro et Multi');
      return;
    }

    try {
      const token = localStorage.getItem('vetToken');
      const response = await fetch(`${API_URL}/api/schedules/recurring-slots`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newRecurringSlot)
      });

      if (response.ok) {
        toast.success('Créneau récurrent créé');
        setShowRecurringModal(false);
        if (onRecurringSlotSave) onRecurringSlotSave();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error saving recurring slot:', error);
      toast.error('Erreur réseau');
    }
  };

  // Format header title
  const getHeaderTitle = () => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const start = weekDates[0];
      const end = weekDates[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.getDate()} - ${end.getDate()} ${MONTHS_FR[start.getMonth()]} ${start.getFullYear()}`;
      } else {
        return `${start.getDate()} ${MONTHS_FR[start.getMonth()]} - ${end.getDate()} ${MONTHS_FR[end.getMonth()]} ${end.getFullYear()}`;
      }
    } else {
      return `${MONTHS_FR[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
  };

  // Render day view
  const renderDayView = () => (
    <div className="overflow-auto max-h-[600px]">
      <div className="grid grid-cols-[80px_1fr] divide-x">
        {/* Time column */}
        <div className="bg-gray-50">
          {timeSlots.map(time => (
            <div key={time} className="h-12 flex items-center justify-end pr-2 text-xs text-gray-500 border-b">
              {time}
            </div>
          ))}
        </div>
        
        {/* Slots column */}
        <div className={`${isBlockedDate(currentDate) || !isDayOpen(currentDate) ? 'bg-gray-100 opacity-50' : ''}`}>
          {timeSlots.map(time => {
            const appts = getAppointmentsForSlot(currentDate, time);
            const isBlocked = isBlockedDate(currentDate) || !isDayOpen(currentDate);
            
            return (
              <div 
                key={time}
                onClick={() => !isBlocked && handleSlotClick(currentDate, time)}
                className={`h-12 border-b px-2 transition-colors ${
                  isBlocked ? 'cursor-not-allowed' :
                  appts.length > 0 ? 'bg-green-50 cursor-pointer' : 'hover:bg-blue-50 cursor-pointer'
                }`}
              >
                {appts.map((apt, i) => (
                  <div 
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAppointmentClick?.(apt);
                    }}
                    className="bg-green-500 text-white text-xs px-2 py-1 rounded truncate"
                  >
                    {apt.patient_name} - {apt.animal_name}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Render week view
  const renderWeekView = () => (
    <div className="overflow-auto max-h-[600px]">
      <div className="grid grid-cols-[80px_repeat(7,1fr)] divide-x">
        {/* Header row */}
        <div className="sticky top-0 z-10 bg-white border-b h-12"></div>
        {weekDates.map((date, i) => {
          const isToday = date.toDateString() === new Date().toDateString();
          const apptCount = getAppointmentsForDate(date).length;
          return (
            <div 
              key={i}
              className={`sticky top-0 z-10 bg-white border-b h-12 flex flex-col items-center justify-center ${
                isToday ? 'bg-green-50' : ''
              }`}
            >
              <span className="text-xs text-gray-500">{DAYS_FR[date.getDay()]}</span>
              <span className={`text-sm font-medium ${isToday ? 'bg-green-500 text-white w-7 h-7 rounded-full flex items-center justify-center' : ''}`}>
                {date.getDate()}
              </span>
              {apptCount > 0 && (
                <span className="text-xs text-green-600">{apptCount} RDV</span>
              )}
            </div>
          );
        })}
        
        {/* Time slots */}
        {timeSlots.map(time => (
          <React.Fragment key={time}>
            {/* Time label */}
            <div className="h-10 flex items-center justify-end pr-2 text-xs text-gray-500 border-b bg-gray-50">
              {time}
            </div>
            
            {/* Day slots */}
            {weekDates.map((date, i) => {
              const appts = getAppointmentsForSlot(date, time);
              const isBlocked = isBlockedDate(date) || !isDayOpen(date);
              const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
              const recurringForThisSlot = recurringSlots.filter(
                s => s.day_of_week === dayOfWeek && s.start_time === time
              );
              
              return (
                <div 
                  key={i}
                  onClick={() => !isBlocked && handleSlotClick(date, time)}
                  className={`h-10 border-b px-1 text-xs transition-colors ${
                    isBlocked ? 'bg-gray-100 cursor-not-allowed' :
                    appts.length > 0 ? 'bg-green-50 cursor-pointer' :
                    recurringForThisSlot.length > 0 ? 'bg-blue-50 cursor-pointer' :
                    'hover:bg-blue-50 cursor-pointer'
                  }`}
                >
                  {appts.map((apt, j) => (
                    <div 
                      key={j}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAppointmentClick?.(apt);
                      }}
                      className="bg-green-500 text-white px-1 py-0.5 rounded truncate text-[10px]"
                    >
                      {apt.patient_name?.split(' ')[0]}
                    </div>
                  ))}
                  {recurringForThisSlot.length > 0 && appts.length === 0 && (
                    <div className="flex items-center gap-1 text-blue-600">
                      <Repeat className="w-3 h-3" />
                      <span className="truncate">{recurringForThisSlot[0].slot_type}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // Render month view
  const renderMonthView = () => (
    <div>
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b">
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
          <div key={day} className="py-2 text-center text-sm font-medium text-gray-600">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {monthDates.map(({ date, isCurrentMonth }, i) => {
          const isToday = date.toDateString() === new Date().toDateString();
          const appts = getAppointmentsForDate(date);
          const isBlocked = isBlockedDate(date) || !isDayOpen(date);
          
          return (
            <div 
              key={i}
              onClick={() => !isBlocked && setCurrentDate(date)}
              className={`min-h-[100px] p-2 border-b border-r cursor-pointer transition-colors ${
                isCurrentMonth ? '' : 'bg-gray-50 text-gray-400'
              } ${isBlocked ? 'bg-gray-100' : 'hover:bg-blue-50'}`}
            >
              <div className={`text-sm mb-1 ${
                isToday ? 'bg-green-500 text-white w-6 h-6 rounded-full flex items-center justify-center' : ''
              }`}>
                {date.getDate()}
              </div>
              
              {appts.slice(0, 3).map((apt, j) => (
                <div 
                  key={j}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAppointmentClick?.(apt);
                  }}
                  className="bg-green-100 text-green-800 text-xs px-1 py-0.5 rounded mb-1 truncate"
                >
                  {apt.time} {apt.patient_name?.split(' ')[0]}
                </div>
              ))}
              
              {appts.length > 3 && (
                <div className="text-xs text-gray-500">+{appts.length - 3} autres</div>
              )}
              
              {isBlocked && (
                <div className="text-xs text-red-500 flex items-center gap-1">
                  <X className="w-3 h-3" /> Fermé
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border" data-testid="agenda-calendar">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate(-1)}
            data-testid="nav-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate(1)}
            data-testid="nav-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={goToToday}
            data-testid="go-today"
          >
            Aujourd'hui
          </Button>
          <h3 className="ml-4 text-lg font-semibold">{getHeaderTitle()}</h3>
        </div>
        
        <div className="flex items-center gap-2">
          {/* View mode toggle - only for Pro/Multi */}
          {hasAdvancedFeatures ? (
            <>
              <div className="flex border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('day')}
                  className={`px-3 py-1.5 text-sm ${viewMode === 'day' ? 'bg-green-500 text-white' : 'bg-white'}`}
                  data-testid="view-day"
                >
                  Jour
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-3 py-1.5 text-sm border-x ${viewMode === 'week' ? 'bg-green-500 text-white' : 'bg-white'}`}
                  data-testid="view-week"
                >
                  Semaine
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-3 py-1.5 text-sm ${viewMode === 'month' ? 'bg-green-500 text-white' : 'bg-white'}`}
                  data-testid="view-month"
                >
                  Mois
                </button>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRecurringModal(true)}
                className="flex items-center gap-1"
                data-testid="add-recurring-btn"
              >
                <Repeat className="w-4 h-4" />
                Créneaux récurrents
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg">
              <CalendarDays className="w-4 h-4" />
              Vue semaine/mois avec Pro
            </div>
          )}
        </div>
      </div>
      
      {/* Calendar content */}
      <div className="p-2">
        {viewMode === 'day' && renderDayView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
      </div>
      
      {/* Recurring slots modal */}
      {showRecurringModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Repeat className="w-5 h-5 text-green-500" />
                Nouveau créneau récurrent
              </h3>
              <button onClick={() => setShowRecurringModal(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Jour de la semaine</Label>
                <select
                  value={newRecurringSlot.day_of_week}
                  onChange={(e) => setNewRecurringSlot({
                    ...newRecurringSlot,
                    day_of_week: parseInt(e.target.value)
                  })}
                  className="w-full mt-1 border rounded-lg p-2"
                  data-testid="recurring-day-select"
                >
                  {DAYS_FULL_FR.map((day, i) => (
                    <option key={i} value={i === 0 ? 6 : i - 1}>{day}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Heure début</Label>
                  <Input
                    type="time"
                    value={newRecurringSlot.start_time}
                    onChange={(e) => setNewRecurringSlot({
                      ...newRecurringSlot,
                      start_time: e.target.value
                    })}
                    className="mt-1"
                    data-testid="recurring-start-time"
                  />
                </div>
                <div>
                  <Label>Heure fin</Label>
                  <Input
                    type="time"
                    value={newRecurringSlot.end_time}
                    onChange={(e) => setNewRecurringSlot({
                      ...newRecurringSlot,
                      end_time: e.target.value
                    })}
                    className="mt-1"
                    data-testid="recurring-end-time"
                  />
                </div>
              </div>
              
              <div>
                <Label>Type de créneau</Label>
                <select
                  value={newRecurringSlot.slot_type}
                  onChange={(e) => setNewRecurringSlot({
                    ...newRecurringSlot,
                    slot_type: e.target.value
                  })}
                  className="w-full mt-1 border rounded-lg p-2"
                  data-testid="recurring-type-select"
                >
                  <option value="consultation">Consultation</option>
                  <option value="vaccination">Vaccination</option>
                  <option value="chirurgie">Chirurgie</option>
                  <option value="urgence">Urgence</option>
                  <option value="teleconsultation">Téléconsultation</option>
                </select>
              </div>
              
              <div className="flex items-center justify-between py-2">
                <Label>Répéter chaque semaine</Label>
                <Switch
                  checked={newRecurringSlot.repeat_weekly}
                  onCheckedChange={(checked) => setNewRecurringSlot({
                    ...newRecurringSlot,
                    repeat_weekly: checked
                  })}
                  data-testid="recurring-weekly-switch"
                />
              </div>
              
              <Button
                onClick={handleSaveRecurringSlot}
                className="w-full bg-green-500 hover:bg-green-600 text-white"
                data-testid="save-recurring-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Créer le créneau
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendaCalendar;
